import React, { useState, useEffect } from 'react';

export default function RecipeForm({ recipe, categories, initialData, onSubmit, onCancel }) {
  // Helper to convert scanned ingredients to form format
  const formatIngredients = (ingredients) => {
    if (!ingredients) return [];
    return ingredients.map((ing, index) => ({
      id: `new-${index}`,
      quantity: ing.quantity || '',
      unit: ing.unit || '',
      name: ing.name || '',
      notes: ing.notes || '',
      sort_order: index,
    }));
  };

  const getInitialData = () => {
    // Use initialData (from scanner) if provided, otherwise use recipe (for editing)
    const source = initialData || recipe;
    return {
      title: source?.title || '',
      description: source?.description || '',
      instructions: source?.instructions || '',
      servings: source?.servings || '',
      prep_time_minutes: source?.prep_time_minutes || '',
      cook_time_minutes: source?.cook_time_minutes || '',
      source_url: source?.source_url || '',
      notes: source?.notes || '',
      is_public: source?.is_public || false,
      category_ids: recipe?.categories?.map((c) => c.id) || [],
      ingredients: initialData ? formatIngredients(initialData.ingredients) : (recipe?.ingredients || []),
    };
  };

  const [formData, setFormData] = useState(getInitialData);

  // Reset form when initialData changes (e.g., from scanner)
  useEffect(() => {
    if (initialData) {
      setFormData(getInitialData());
    }
  }, [initialData]);
  const [submitting, setSubmitting] = useState(false);
  const [newIngredient, setNewIngredient] = useState({ quantity: '', unit: '', name: '' });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData((prev) => ({
      ...prev,
      category_ids: prev.category_ids.includes(categoryId)
        ? prev.category_ids.filter((id) => id !== categoryId)
        : [...prev.category_ids, categoryId],
    }));
  };

  const handleAddIngredient = () => {
    if (!newIngredient.name.trim()) return;
    setFormData((prev) => ({
      ...prev,
      ingredients: [
        ...prev.ingredients,
        { ...newIngredient, id: `new-${Date.now()}`, sort_order: prev.ingredients.length },
      ],
    }));
    setNewIngredient({ quantity: '', unit: '', name: '' });
  };

  const handleRemoveIngredient = (index) => {
    setFormData((prev) => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Title *
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          placeholder="Recipe name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          placeholder="Brief description of the recipe"
        />
      </div>

      {/* Ingredients */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Ingredients
        </label>
        {formData.ingredients.length > 0 && (
          <ul className="mb-3 space-y-1">
            {formData.ingredients.map((ing, index) => (
              <li
                key={ing.id || index}
                className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded"
              >
                <span className="text-gray-900 dark:text-white">
                  {ing.quantity} {ing.unit} {ing.name}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveIngredient(index)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Qty"
            value={newIngredient.quantity}
            onChange={(e) => setNewIngredient((prev) => ({ ...prev, quantity: e.target.value }))}
            className="w-16 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-sm"
          />
          <input
            type="text"
            placeholder="Unit"
            value={newIngredient.unit}
            onChange={(e) => setNewIngredient((prev) => ({ ...prev, unit: e.target.value }))}
            className="w-20 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-sm"
          />
          <input
            type="text"
            placeholder="Ingredient name"
            value={newIngredient.name}
            onChange={(e) => setNewIngredient((prev) => ({ ...prev, name: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddIngredient())}
            className="flex-1 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-sm"
          />
          <button
            type="button"
            onClick={handleAddIngredient}
            className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
          >
            Add
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Servings
          </label>
          <input
            type="number"
            name="servings"
            value={formData.servings}
            onChange={handleChange}
            min="1"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Prep Time (min)
          </label>
          <input
            type="number"
            name="prep_time_minutes"
            value={formData.prep_time_minutes}
            onChange={handleChange}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Cook Time (min)
          </label>
          <input
            type="number"
            name="cook_time_minutes"
            value={formData.cook_time_minutes}
            onChange={handleChange}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Instructions
        </label>
        <textarea
          name="instructions"
          value={formData.instructions}
          onChange={handleChange}
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          placeholder="Step-by-step instructions..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Source URL
        </label>
        <input
          type="url"
          name="source_url"
          value={formData.source_url}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          placeholder="https://..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Notes
        </label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          placeholder="Personal notes, variations, tips..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Categories
        </label>
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => handleCategoryToggle(cat.id)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                formData.category_ids.includes(cat.id)
                  ? 'ring-2 ring-offset-2'
                  : 'opacity-60 hover:opacity-100'
              }`}
              style={{
                backgroundColor: cat.color + '20',
                color: cat.color,
                ringColor: cat.color,
              }}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="is_public"
          name="is_public"
          checked={formData.is_public}
          onChange={handleChange}
          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
        />
        <label htmlFor="is_public" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
          Make this recipe public (visible to everyone)
        </label>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {submitting ? 'Saving...' : recipe ? 'Update Recipe' : 'Create Recipe'}
        </button>
      </div>
    </form>
  );
}
